import React, { useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../../services/api';

const FIELDS = [
    { name: 'name',          label: 'Company / Client Name', required: true,  span: 2 },
    { name: 'altName',       label: 'Alt Name',              required: false, span: 1 },
    { name: 'contactPerson', label: 'Contact Person',        required: false, span: 1 },
    { name: 'phone',         label: 'Phone',                 required: false, span: 1 },
    { name: 'email',         label: 'Email',                 required: false, span: 1 },
    { name: 'gst',           label: 'GST Number',            required: false, span: 1 },
    { name: 'address',       label: 'Address',               required: false, span: 2, multiline: true },
    { name: 'notes',         label: 'Notes',                 required: false, span: 2, multiline: true },
];

const empty = () => ({ name: '', altName: '', contactPerson: '', phone: '', email: '', gst: '', address: '', notes: '' });

const AddClientModal = ({ isOpen, initialName = '', token, onSuccess, onClose }) => {
    const [form, setForm]     = useState({ ...empty(), name: initialName });
    const [saving, setSaving] = useState(false);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) return;
        setSaving(true);
        try {
            const res = await api.post(`/clients`, form);
            toast.success(`Client "${res.data.name}" added!`);
            onSuccess(res.data);
            setForm(empty());
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to add client');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 z-[9999]">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <h2 className="text-lg font-bold text-gray-800">Add New Client</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl leading-none transition-colors"
                    >
                        ×
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="px-6 py-5">
                    <div className="grid grid-cols-2 gap-4">
                        {FIELDS.map(f => (
                            <div key={f.name} className={f.span === 2 ? 'col-span-2' : 'col-span-1'}>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                    {f.label} {f.required && <span className="text-red-500">*</span>}
                                </label>
                                {f.multiline ? (
                                    <textarea
                                        name={f.name}
                                        value={form[f.name]}
                                        onChange={handleChange}
                                        rows={2}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none"
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        name={f.name}
                                        value={form[f.name]}
                                        onChange={handleChange}
                                        required={f.required}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                                    />
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="flex gap-3 mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving || !form.name.trim()}
                            className="flex-1 py-2 rounded-lg bg-gray-900 hover:bg-gray-700 disabled:opacity-40 text-white text-sm font-medium transition-colors"
                        >
                            {saving ? 'Saving…' : 'Add Client'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddClientModal;